import React, { useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { GetAction } from './GetAction'
import {
  DEFAULT_HEADER_MENU,
  buildMenuTree,
  isExternalUrl,
  menuHref,
  pagesToMenuItems,
} from '../utils/pageMenu'
import { canAccessManage } from '../manage/auth'
import { mediaUrl } from '../utils/mediaUrl'
import { AUTH_EVENT, clearSession } from '../utils/sessionAuth'
import ManageTopNav from './ManageTopNav'

const DEFAULT_WEBSITE_NAME = 'श्री आचार्य विद्यासागर जीवदया ट्रस्ट'

function NavMenuLink({ item, className, style }) {
  const href = menuHref(item.url)
  if (!href || href === '#') {
    return (
      <span className={className} style={style}>
        {item.label}
      </span>
    )
  }
  if (isExternalUrl(href) || Number(item.open_new_tab) === 1) {
    return (
      <a
        className={className}
        style={style}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {item.label}
      </a>
    )
  }
  return (
    <Link className={className} style={style} to={href}>
      {item.label}
    </Link>
  )
}

function DropdownItemLink({ item, onNavigate }) {
  const href = menuHref(item.url)
  const close = () => {
    if (onNavigate) onNavigate()
  }
  if (!href || href === '#') {
    return (
      <span className="header-dd-item header-dd-item-static" onClick={close}>
        {item.label}
      </span>
    )
  }
  if (isExternalUrl(href) || Number(item.open_new_tab) === 1) {
    return (
      <a
        className="header-dd-item"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={close}
      >
        {item.label}
      </a>
    )
  }
  return (
    <Link className="header-dd-item" to={href} onClick={close}>
      {item.label}
    </Link>
  )
}

function HeaderNavItem({ item, navLinkClass, navLinkStyle }) {
  const children = Array.isArray(item.children) ? item.children : []
  const hasDropdown = children.length > 0
  const key = item.menu_item_id || `${item.label}-${item.url}`
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const closeTimer = useRef(null)

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const isDesktop = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 768px)').matches

  const openMenu = () => {
    clearCloseTimer()
    setOpen(true)
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimer.current = window.setTimeout(() => setOpen(false), 180)
  }

  useEffect(() => {
    if (!hasDropdown || !open) return undefined
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [hasDropdown, open])

  useEffect(() => () => clearCloseTimer(), [])

  if (!hasDropdown) {
    return (
      <li className="nav-item">
        <NavMenuLink item={item} className={navLinkClass} style={navLinkStyle} />
      </li>
    )
  }

  return (
    <li
      className={`nav-item header-menu-dropdown${open ? ' header-menu-open' : ''}`}
      ref={wrapRef}
      onMouseEnter={() => {
        if (isDesktop()) openMenu()
      }}
      onMouseLeave={() => {
        if (isDesktop()) scheduleClose()
      }}
    >
      <button
        type="button"
        className={`${navLinkClass} header-menu-dropdown-toggle${
          open ? ' header-menu-open' : ''
        }`}
        style={{ ...navLinkStyle, cursor: 'pointer' }}
        aria-expanded={open ? 'true' : 'false'}
        aria-haspopup="true"
        id={`header-dd-${key}`}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          clearCloseTimer()
          setOpen((v) => !v)
        }}
      >
        {item.label}
      </button>
      <ul
        className={`header-dropdown-menu header-menu-dropdown-panel header-submenu${
          open ? ' header-menu-open' : ''
        }`}
        aria-labelledby={`header-dd-${key}`}
        role="menu"
        onMouseEnter={() => {
          if (isDesktop()) openMenu()
        }}
        onMouseLeave={() => {
          if (isDesktop()) scheduleClose()
        }}
      >
        {children.map((child, idx) => (
          <li
            key={child.menu_item_id || `${child.label}-${idx}`}
            className="header-submenu-item"
            role="none"
          >
            <DropdownItemLink item={child} onNavigate={() => setOpen(false)} />
          </li>
        ))}
      </ul>
    </li>
  )
}

function Header() {
  const history = useHistory()
  const [websiteName, setWebsiteName] = useState(DEFAULT_WEBSITE_NAME)
  const [logoPath, setLogoPath] = useState('')
  const [showHeaderName, setShowHeaderName] = useState(true)
  const [showHeaderLogo, setShowHeaderLogo] = useState(false)
  const [menuItems, setMenuItems] = useState(null)
  const [showManage, setShowManage] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)

  useEffect(() => {
    const refreshManage = () => setShowManage(canAccessManage())
    refreshManage()
    window.addEventListener(AUTH_EVENT, refreshManage)
    return () => window.removeEventListener(AUTH_EVENT, refreshManage)
  }, [])

  useEffect(() => {
    GetAction('get', {}, 'settings/get').then((data) => {
      if (data?.status === 'success' && data.response) {
        const r = data.response
        if (r.website_name) {
          setWebsiteName(r.website_name)
        }
        setLogoPath(mediaUrl(r.logo_path || ''))
        setShowHeaderName(Number(r.show_header_name ?? 1) === 1)
        setShowHeaderLogo(Number(r.show_header_logo ?? 0) === 1)
      }
    })

    let cancelled = false
    ;(async () => {
      const [menuData, pagesData] = await Promise.all([
        GetAction('get', { location: 'header' }, 'menu/list'),
        GetAction('get', { menu_location: 'header' }, 'pages/pagesList'),
      ])
      if (cancelled) return

      const pages =
        pagesData?.status === 'success' && Array.isArray(pagesData.response)
          ? pagesData.response
          : []

      if (
        menuData?.status === 'success' &&
        Array.isArray(menuData.response) &&
        menuData.response.length > 0
      ) {
        setMenuItems(buildMenuTree(menuData.response))
        return
      }

      const pageItems = pagesToMenuItems(pages)
      const defaults = [...DEFAULT_HEADER_MENU]
      const home = defaults[0]
      const rest = defaults.slice(1)
      setMenuItems(buildMenuTree([home, ...pageItems, ...rest]))
    })()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  const logout = () => {
    clearSession()
    setUserMenuOpen(false)
    history.push('/Login')
  }

  const navLinkClass = 'nav-link header-nav-link'
  const navLinkStyle = { color: '#fff' }
  const links = menuItems || buildMenuTree(DEFAULT_HEADER_MENU)

  return (
    <>
      {showManage ? <ManageTopNav /> : null}
      <nav className="navbar navbar-expand-md navbar-dark header">
      <Link
        className="navbar-brand websitename"
        to="/"
        style={navLinkStyle}
        aria-label={websiteName || 'Home'}
      >
        {showHeaderLogo && logoPath ? (
          <img src={logoPath} alt="" className="header-brand-logo" />
        ) : null}
        {showHeaderName ? <b>{websiteName}</b> : null}
      </Link>
      <button
        className="navbar-toggler header-toggler"
        type="button"
        data-toggle="collapse"
        data-target="#navbarCollapse"
        aria-controls="navbarCollapse"
        aria-expanded="false"
        aria-label="Toggle navigation"
      >
        <span className="header-toggler-bar" />
        <span className="header-toggler-bar" />
        <span className="header-toggler-bar" />
      </button>
      <div className="collapse navbar-collapse header-nav-collapse" id="navbarCollapse">
        <ul className="navbar-nav ml-auto header-nav">
          {links.map((item, idx) => (
            <HeaderNavItem
              key={item.menu_item_id || `${item.label}-${idx}`}
              item={item}
              navLinkClass={navLinkClass}
              navLinkStyle={navLinkStyle}
            />
          ))}

          {sessionStorage.id ? (
            <li
              className={`nav-item header-menu-dropdown${
                userMenuOpen ? ' header-menu-open' : ''
              }`}
              ref={userMenuRef}
            >
              <button
                type="button"
                className={`nav-link header-nav-link header-user-toggle header-menu-dropdown-toggle${
                  userMenuOpen ? ' header-menu-open' : ''
                }`}
                style={{ ...navLinkStyle, cursor: 'pointer' }}
                aria-expanded={userMenuOpen ? 'true' : 'false'}
                aria-haspopup="true"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setUserMenuOpen((v) => !v)
                }}
              >
                {sessionStorage.name}
              </button>
              <ul
                className={`header-dropdown-menu header-menu-dropdown-panel${
                  userMenuOpen ? ' header-menu-open' : ''
                }`}
                role="menu"
              >
                <li role="none">
                  <Link
                    className="header-dd-item"
                    to="/Profile"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Profile
                  </Link>
                </li>
                <li role="none">
                  <Link
                    className="header-dd-item"
                    to="/ChangePassword"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Change password
                  </Link>
                </li>
                <li role="none">
                  <Link
                    className="header-dd-item"
                    to="/DonationHistory"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Donation History
                  </Link>
                </li>
                {showManage ? (
                  <li role="none">
                    <Link
                      className="header-dd-item"
                      to="/manage"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Manage
                    </Link>
                  </li>
                ) : null}
                <li role="none">
                  <hr className="header-dd-divider" />
                </li>
                <li role="none">
                  <button
                    type="button"
                    className="header-dd-item header-logout-btn"
                    onClick={logout}
                  >
                    Logout
                  </button>
                </li>
              </ul>
            </li>
          ) : (
            <>
              <li className="nav-item">
                <Link className={navLinkClass} style={navLinkStyle} to="/Login">
                  Login
                </Link>
              </li>
              <li className="nav-item">
                <Link className={navLinkClass} style={navLinkStyle} to="/SignUp">
                  Sign Up
                </Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </nav>
    </>
  )
}

export default Header
